/**
 * PHONE INPUT WITH COUNTRY CODE
 * 
 * Reusable phone input component with:
 * - Country code dropdown (defaults to +27 South Africa)
 * - Validation for exactly 9 digits after country code
 * - WhatsApp verification and icon display
 * - wa.me link integration
 * 
 * @version 1.0.0
 * @date December 2025
 */

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Phone, AlertCircle, Loader2 } from 'lucide-react';
import { authService } from '@/lib/authService';
import { buildPhoneHref } from '@/lib/phoneUtils';

// ============================================================================
// TYPES
// ============================================================================

interface CountryCode {
  code: string;
  country: string;
  flag: string;
  digits: number; // Expected digits after country code
}

interface PhoneInputWithCountryCodeProps {
  value: string | null;
  onChange: (fullNumber: string | null) => void;
  label?: string | React.ReactNode;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  required?: boolean;
  isDark?: boolean;
  showWhatsAppIcon?: boolean;
  onWhatsAppStatusChange?: (hasWhatsApp: boolean) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const COUNTRY_CODES: CountryCode[] = [
  { code: '+27', country: 'South Africa', flag: '🇿🇦', digits: 9 },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸', digits: 10 },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', digits: 10 },
  { code: '+61', country: 'Australia', flag: '🇦🇺', digits: 9 },
  { code: '+49', country: 'Germany', flag: '🇩🇪', digits: 10 },
  { code: '+33', country: 'France', flag: '🇫🇷', digits: 9 },
  { code: '+91', country: 'India', flag: '🇮🇳', digits: 10 },
  { code: '+86', country: 'China', flag: '🇨🇳', digits: 11 },
  { code: '+81', country: 'Japan', flag: '🇯🇵', digits: 10 },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', digits: 11 },
  { code: '+52', country: 'Mexico', flag: '🇲🇽', digits: 10 },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', digits: 10 },
  { code: '+254', country: 'Kenya', flag: '🇰🇪', digits: 9 },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼', digits: 9 },
  { code: '+267', country: 'Botswana', flag: '🇧🇼', digits: 8 },
  { code: '+264', country: 'Namibia', flag: '🇳🇦', digits: 9 },
  { code: '+266', country: 'Lesotho', flag: '🇱🇸', digits: 8 },
  { code: '+268', country: 'Eswatini', flag: '🇸🇿', digits: 8 },
  { code: '+258', country: 'Mozambique', flag: '🇲🇿', digits: 9 },
  { code: '+260', country: 'Zambia', flag: '🇿🇲', digits: 9 },
  { code: '+971', country: 'UAE', flag: '🇦🇪', digits: 9 },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', digits: 9 },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', digits: 8 },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', digits: 9 },
  { code: '+34', country: 'Spain', flag: '🇪🇸', digits: 9 },
  { code: '+39', country: 'Italy', flag: '🇮🇹', digits: 10 },
  { code: '+351', country: 'Portugal', flag: '🇵🇹', digits: 9 },
  { code: '+48', country: 'Poland', flag: '🇵🇱', digits: 9 },
];

// WhatsApp icon SVG component
const WhatsAppIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse a full phone number into country code and local number
 */
function parsePhoneNumber(fullNumber: string | null): { countryCode: string; localNumber: string } {
  if (!fullNumber) {
    return { countryCode: '+27', localNumber: '' };
  }

  const cleaned = fullNumber.replace(/[\s\-\(\)]/g, '');
  
  // Try to match known country codes (longest first)
  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  
  for (const cc of sortedCodes) {
    if (cleaned.startsWith(cc.code)) {
      return {
        countryCode: cc.code,
        localNumber: cleaned.slice(cc.code.length)
      };
    }
  }

  // If starts with 0, assume South Africa and strip leading 0
  if (cleaned.startsWith('0')) {
    return {
      countryCode: '+27',
      localNumber: cleaned.slice(1)
    };
  }

  // Default to +27 with the whole number as local
  return {
    countryCode: '+27',
    localNumber: cleaned.replace(/^\+/, '')
  };
}

/**
 * Format a full phone number for display
 */
function formatPhoneNumber(countryCode: string, localNumber: string): string {
  if (!localNumber) return '';
  return `${countryCode}${localNumber}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function PhoneInputWithCountryCode({
  value,
  onChange,
  label,
  placeholder = 'Enter phone number',
  className = '',
  inputClassName = '',
  labelClassName = '',
  disabled = false,
  required = false,
  isDark = false,
  showWhatsAppIcon = true,
  onWhatsAppStatusChange
}: PhoneInputWithCountryCodeProps) {
  // Parse the initial value
  const parsed = useMemo(() => parsePhoneNumber(value), [value]);
  
  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);
  const [hasWhatsApp, setHasWhatsApp] = useState<boolean | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verifyAbortRef = useRef<AbortController | null>(null);

  // Sync internal state when value prop changes (e.g., when loading existing data)
  useEffect(() => {
    const newParsed = parsePhoneNumber(value);
    setCountryCode(newParsed.countryCode);
    setLocalNumber(newParsed.localNumber);
  }, [value]);

  // Get the selected country's expected digit count
  const selectedCountry = useMemo(() => 
    COUNTRY_CODES.find(cc => cc.code === countryCode) || COUNTRY_CODES[0],
    [countryCode]
  );

  // Validate local number length
  const isValidLength = useMemo(() => {
    if (!localNumber) return true;
    return localNumber.length === selectedCountry.digits;
  }, [localNumber, selectedCountry.digits]);

  // Handle country code change
  const handleCountryCodeChange = useCallback((newCode: string) => {
    setCountryCode(newCode);
    setHasWhatsApp(null);
    setError(null);
    
    if (localNumber) {
      onChange(formatPhoneNumber(newCode, localNumber));
    }
  }, [localNumber, onChange]);

  // Handle local number input
  const handleLocalNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const digits = e.target.value.replace(/\D/g, '');
    
    // Limit to expected digit count + some buffer
    const maxDigits = selectedCountry.digits + 2;
    const truncated = digits.slice(0, maxDigits);
    
    setLocalNumber(truncated);
    setHasWhatsApp(null);
    setError(null);
    
    if (truncated) {
      onChange(formatPhoneNumber(countryCode, truncated));
    } else {
      onChange(null);
    }
  }, [countryCode, selectedCountry.digits, onChange]);

  // Verify WhatsApp status. The API route consults the DB cache first; pass
  // forceRefresh=true to bypass the cache.
  const verifyWhatsApp = useCallback(async (forceRefresh = false) => {
    if (!localNumber || !isValidLength) return;

    const fullNumber = formatPhoneNumber(countryCode, localNumber);
    if (!fullNumber) return;

    // Cancel any in-flight request
    verifyAbortRef.current?.abort();
    const controller = new AbortController();
    verifyAbortRef.current = controller;

    setIsVerifying(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/whatsapp/verify-number', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phone: fullNumber, forceRefresh }),
        signal: controller.signal,
      });

      if (response.ok) {
        const data = await response.json();
        // API returns isWhatsApp field
        const hasWA = data.isWhatsApp === true;
        setHasWhatsApp(hasWA);
        onWhatsAppStatusChange?.(hasWA);
      } else {
        setHasWhatsApp(false);
        onWhatsAppStatusChange?.(false);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return; // cancelled, don't update state
      console.error('WhatsApp verification failed:', err);
      setHasWhatsApp(false);
      onWhatsAppStatusChange?.(false);
    } finally {
      setIsVerifying(false);
    }
  }, [countryCode, localNumber, isValidLength, onWhatsAppStatusChange]);

  // Auto-verify on blur if valid
  const handleBlur = useCallback(() => {
    if (isValidLength && localNumber && showWhatsAppIcon) {
      verifyWhatsApp();
    }
    
    // Set validation error if invalid length
    if (localNumber && !isValidLength) {
      setError(`Please enter exactly ${selectedCountry.digits} digits for ${selectedCountry.country}`);
    }
  }, [isValidLength, localNumber, showWhatsAppIcon, verifyWhatsApp, selectedCountry]);

  // Generate WhatsApp link
  const whatsAppLink = useMemo(() => {
    if (!hasWhatsApp || !localNumber) return null;
    const fullNumber = formatPhoneNumber(countryCode, localNumber).replace('+', '');
    return `https://wa.me/${fullNumber}`;
  }, [hasWhatsApp, countryCode, localNumber]);

  // Base input styles
  const baseInputClass = isDark
    ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';

  const baseLabelClass = isDark
    ? 'text-gray-400'
    : 'text-gray-500';

  return (
    <div className={`${className}`}>
      {label && (
        <label className={`block text-xs font-medium mb-1 ${baseLabelClass} ${labelClassName}`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="flex gap-2">
        {/* Country Code Dropdown */}
        <div className="relative">
          <select
            value={countryCode}
            onChange={(e) => handleCountryCodeChange(e.target.value)}
            disabled={disabled}
            className={`w-28 px-2 py-2 rounded-lg border text-sm appearance-none cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${baseInputClass} ${inputClassName}`}
            style={{ backgroundImage: 'none' }}
          >
            {COUNTRY_CODES.map((cc) => (
              <option key={cc.code} value={cc.code}>
                {cc.flag} {cc.code}
              </option>
            ))}
          </select>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Phone Number Input */}
        <div className="flex-1 relative">
          <input
            type="tel"
            value={localNumber}
            onChange={handleLocalNumberChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${baseInputClass} ${inputClassName} ${
              error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
            }`}
          />
          
          {/* Status Icons */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isVerifying && (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            )}
            
            {!isVerifying && hasWhatsApp && whatsAppLink && showWhatsAppIcon && (
              <a
                href={whatsAppLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-600 transition-colors"
                title="Chat on WhatsApp"
              >
                <WhatsAppIcon className="w-5 h-5" />
              </a>
            )}
            
            {!isVerifying && hasWhatsApp === false && localNumber && isValidLength && showWhatsAppIcon && (
              <span title="No WhatsApp">
                <Phone className="w-4 h-4 text-gray-400" />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Validation / Helper Text */}
      <div className="mt-1 flex items-center justify-between">
        {error ? (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        ) : localNumber ? (
          <p className={`text-xs ${isValidLength ? 'text-green-600' : 'text-amber-500'}`}>
            {localNumber.length} / {selectedCountry.digits} digits
            {isValidLength && ' ✓'}
          </p>
        ) : (
          <p className="text-xs text-gray-400">
            {selectedCountry.digits} digits required for {selectedCountry.country}
          </p>
        )}
        
        {hasWhatsApp && whatsAppLink && (
          <a
            href={whatsAppLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-green-600 hover:underline flex items-center gap-1"
          >
            <WhatsAppIcon className="w-3 h-3" />
            WhatsApp available
          </a>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPACT VERSION (for inline use in tables, etc.)
// ============================================================================

interface PhoneDisplayWithWhatsAppProps {
  phone: string | null;
  showWhatsAppIcon?: boolean;
  isDark?: boolean;
  className?: string;
  /** Optional pre-known WhatsApp status (e.g. from contact_people.has_whatsapp).
   *  When provided, the component uses it immediately and skips the network
   *  call unless the user clicks to re-check. */
  initialHasWhatsApp?: boolean | null;
  /** Bypass the DB cache and force a live external WhatsApp check. */
  forceRefresh?: boolean;
  /** Never call the verify endpoint — display only the supplied/cached status.
   *  Use in list views; live checks are done by the whatsapp-number-sync cron. */
  cacheOnly?: boolean;
}

export function PhoneDisplayWithWhatsApp({
  phone,
  showWhatsAppIcon = true,
  isDark = false,
  className = '',
  initialHasWhatsApp = null,
  forceRefresh = false,
  cacheOnly = false,
}: PhoneDisplayWithWhatsAppProps) {
  const [hasWhatsApp, setHasWhatsApp] = useState<boolean | null>(initialHasWhatsApp);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    // Reset to the provided initial value whenever the phone changes.
    setHasWhatsApp(initialHasWhatsApp ?? null);

    if (!phone || !showWhatsAppIcon) return;
    // Cache-only mode: never trigger a verify call — the background
    // whatsapp-number-sync cron keeps whatsapp_number_checks/contact_people
    // fresh, so unknown numbers simply render without a WhatsApp icon.
    if (cacheOnly) return;
    // If a known DB status was supplied, trust it and skip the network call.
    if (initialHasWhatsApp !== null && initialHasWhatsApp !== undefined && !forceRefresh) return;

    const verifyWhatsApp = async () => {
      setIsVerifying(true);
      try {
        const token = authService.getTokens()?.access_token;
        const response = await fetch('/api/v1/whatsapp/verify-number', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ phone, forceRefresh })
        });

        if (response.ok) {
          const data = await response.json();
          // API returns isWhatsApp field
          setHasWhatsApp(data.isWhatsApp === true);
        }
      } catch (err) {
        console.error('WhatsApp check failed:', err);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyWhatsApp();
  }, [phone, showWhatsAppIcon, initialHasWhatsApp, forceRefresh, cacheOnly]);

  if (!phone) return <span className="text-gray-400">-</span>;

  const phoneHref = buildPhoneHref(phone, hasWhatsApp);
  const isWhatsAppLink = hasWhatsApp && phoneHref.startsWith('https://wa.me/');

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <a 
        href={phoneHref}
        {...(isWhatsAppLink ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={`hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
        title={isWhatsAppLink ? 'Chat on WhatsApp' : 'Call'}
      >
        {phone}
      </a>
      
      {isVerifying && (
        <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
      )}
      
      {!isVerifying && isWhatsAppLink && (
        <a
          href={phoneHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-green-500 hover:text-green-600 transition-colors"
          title="Chat on WhatsApp"
        >
          <WhatsAppIcon className="w-4 h-4" />
        </a>
      )}
    </span>
  );
}
